import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { ExternalLogsData } from '../../../../data/ExternalLogDemoData';
import LayoutProvider from '../../../Contexts/LayoutProvider';
import { useTemplate } from '../../../features/template/TemplateProvider';
import PageTitle from '../../../utils/PageTitle';
import { mkRelativeUrl } from '../../../utils/URL';
import ExternalLogPageHeader from './Components/ExternalLogPageHeader';
import ExternalLogPageHeaderMobile from './Components/ExternalLogPageHeaderMobile';
import ExternalLogPageTableMobile from './Components/ExternalLogPageTableMobile';
import ExternalLogsPageTable from './Components/ExternalLogsPageTable';
import { ApiExternalLogsList } from './ExternalLogListOperation';

const initialState = {
    externalLogs: ExternalLogsData,
    pagination: {}
};

function ExternalLogListPage() {
    const { setMessage } = useTemplate();
    const navigate = useNavigate();
    const [externalLogsData, setExternalLogsData] = useState(initialState);
    const [reloadKey, setReloadKey] = useState(0);
    const [searchParams] = useSearchParams();
    const [queryStr, setQueryStr] = useState({ q: searchParams.get('q') || '' });

    const p = useMemo(
        () => ({
            q: searchParams.get('q') || '',
            limit: searchParams.get('limit'),
            page: searchParams.get('page')
        }),
        [searchParams]
    );

    const debouncedUpdates = useDebouncedCallback((val) => {
        navigate(mkRelativeUrl(`/logs/external`, { ...p, q: val }));
    }, 800);

    const handleChange = (ev) => {
        const { name, value } = ev.target;
        if (name) {
            if (name === 'q') {
                setQueryStr({ q: value });
                debouncedUpdates(value);
            } else {
                navigate(mkRelativeUrl(`/logs/external`, { ...p, [name]: value }));
            }
        }
    };

    const onChangePage = (pageNumber) => {
        navigate(mkRelativeUrl(`/logs/external`, { ...p, page: pageNumber }));
    };

    const handleReload = () => {
        setReloadKey((cu) => cu + 1);
    };

    useEffect(() => {
        setExternalLogsData(initialState);
        ApiExternalLogsList(p)
            .then(({ data }) => {
                setExternalLogsData(data);
            })
            .catch((err) => {
                setExternalLogsData([]);
                setMessage({ type: 'error', text: err.message });
            });
    }, [reloadKey, setMessage, p]);

    PageTitle('External Logs');
    return (
        <div className="log_file_page inner_page_cont">
            <LayoutProvider>
                <ExternalLogPageHeader
                    layout="default"
                    onChangeHandler={handleChange}
                    handleReload={handleReload}
                    p={{ ...p, ...queryStr, limit: p.limit || 20 }}
                />
                <ExternalLogPageHeaderMobile
                    layout={['md', 'sm', 'xs', 'xxs']}
                    p={{ ...p, ...queryStr, limit: p.limit || 20 }}
                    onChangeHandler={handleChange}
                    handleReload={handleReload}
                />
            </LayoutProvider>
            <LayoutProvider>
                <ExternalLogsPageTable
                    layout="default"
                    data={externalLogsData?.externalLogs}
                    handleReload={handleReload}
                    onChangePage={onChangePage}
                    pagination={externalLogsData?.pagination}
                />
                <ExternalLogPageTableMobile
                    layout={['md', 'sm', 'xs', 'xxs']}
                    data={externalLogsData?.externalLogs}
                    handleReload={handleReload}
                />
            </LayoutProvider>
        </div>
    );
}

export default ExternalLogListPage;
